import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_DESIGN_SYSTEM_MODE,
  getDesignSystemMode,
  writeStoredMode,
  type DesignSystemMode,
} from './design-system-mode';

interface DesignSystemContextValue {
  /** The effective mode after env + storage resolution. */
  mode: DesignSystemMode;
  /** Convenience flag. */
  isModern: boolean;
  /** True when a developer env flag is forcing modern (toggle is locked). */
  envForced: boolean;
  /** Persist + apply a mode (no-op when env-forced). */
  setMode: (mode: DesignSystemMode) => void;
  /** Flip between legacy/modern (no-op when env-forced). */
  toggle: () => void;
}

const DesignSystemContext = createContext<DesignSystemContextValue | undefined>(undefined);

/**
 * DesignSystemProvider — wrap the app once near the root. Defaults to Legacy.
 *
 * It only carries the *presentation choice*. It does NOT change business logic,
 * auth, database writes, or data flow — those stay identical across modes.
 * Pages read `useDesignSystem()` and decide which presentation layer to render;
 * both layers call the same underlying actions/hooks.
 */
export function DesignSystemProvider({
  children,
  envEnabled = false,
  defaultMode = DEFAULT_DESIGN_SYSTEM_MODE,
}: {
  children: ReactNode;
  /** Pass the parsed VITE_ENABLE_MODERN_UI value from the app. */
  envEnabled?: boolean;
  defaultMode?: DesignSystemMode;
}) {
  const [mode, setModeState] = useState<DesignSystemMode>(() =>
    getDesignSystemMode({ envEnabled, stored: undefined }) ?? defaultMode,
  );

  // Re-resolve once on mount in case storage wasn't available during the
  // initializer (SSR / hydration safety).
  useEffect(() => {
    setModeState(getDesignSystemMode({ envEnabled }));
  }, [envEnabled]);

  const setMode = useCallback(
    (next: DesignSystemMode) => {
      if (envEnabled) return; // env override locks the choice
      writeStoredMode(next);
      setModeState(next);
    },
    [envEnabled],
  );

  const toggle = useCallback(() => {
    setMode(mode === 'modern' ? 'legacy' : 'modern');
  }, [mode, setMode]);

  const value = useMemo<DesignSystemContextValue>(
    () => ({
      mode,
      isModern: mode === 'modern',
      envForced: envEnabled,
      setMode,
      toggle,
    }),
    [mode, envEnabled, setMode, toggle],
  );

  return <DesignSystemContext.Provider value={value}>{children}</DesignSystemContext.Provider>;
}

export function useDesignSystem(): DesignSystemContextValue {
  const value = useContext(DesignSystemContext);
  if (!value) {
    throw new Error('useDesignSystem must be used within a DesignSystemProvider');
  }
  return value;
}

/**
 * Safe variant for components that may render outside the provider (e.g. shared
 * widgets). Returns legacy defaults instead of throwing.
 */
export function useDesignSystemSafe(): DesignSystemContextValue {
  const value = useContext(DesignSystemContext);
  return (
    value ?? {
      mode: 'legacy',
      isModern: false,
      envForced: false,
      setMode: () => undefined,
      toggle: () => undefined,
    }
  );
}
